import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AdminVoucher } from '../../../api/voucher/voucherApiTypes';
import InfoTooltip from '../../../components/InfoTooltip';
import { cn } from '../../../utils/utils';
import VoucherStatementPanel from './VoucherStatementPanel';
import VoucherTaxCategorySetting from './VoucherTaxCategorySetting';
import VoucherLiabilityReport from './VoucherLiabilityReport';
import { onRowClick } from '../../../utils/rowClick';

interface PrepaidVouchersTabProps {
    vouchers: AdminVoucher[];
    /** `view:voucher-history`: the statement under a row. */
    canViewHistory: boolean;
    naLabel: string;
    formatMoney: (value: number | null, currency: string | null) => string;
    formatDate: (iso: string | null) => string;
}

const headerCellClass = 'text-left px-4 py-2.5 text-foreground/60 font-medium';
const cellClass = 'px-4 py-2.5 text-foreground/80';
const COLUMNS = 9;

/**
 * The Prepaid tab (#3261): every prepaid voucher with what it is worth and
 * why, the outstanding liability, the voucher tax category and the liability
 * report. Status, liability and refunds are the backend's answer
 * (`prepaidVoucherState`); nothing here re-derives them.
 */
const PrepaidVouchersTab = ({ vouchers, canViewHistory, naLabel, formatMoney, formatDate }: PrepaidVouchersTabProps) => {
    const { t } = useTranslation();
    const [expandedId, setExpandedId] = useState<string | null>(null);

    // Paid, unexpired, not charged back — archived and disputed vouchers
    // included, vouchers without a payment not (ADR-0002 amendment, decision 7).
    const liability = useMemo(
        () => vouchers.reduce((sum, v) => sum + (v.prepaid?.liabilityAmount ?? 0), 0),
        [vouchers],
    );
    // Tenants are single-currency in practice; take it from the first row that has one.
    const currency = vouchers.find((v) => v.currency)?.currency ?? null;

    const vatLabel = (v: AdminVoucher) => {
        const rate = v.prepaid?.vatRate;
        if (rate == null) return naLabel;
        const percent = rate % 1 === 0 ? rate.toFixed(0) : rate.toFixed(2);
        return `${v.prepaid?.vatCountry ?? ''} ${percent}%`.trim();
    };

    return (
        <>
            <VoucherTaxCategorySetting />
            <p className={'text-sm text-foreground/60 mb-3 flex items-center gap-1.5'}>
                <span>{t('vouchers_codes.outstanding_liability')}:</span>
                <span className={'font-semibold text-foreground'}>{formatMoney(liability, currency)}</span>
                <InfoTooltip text={t('vouchers_codes.prepaid_list.liability_help')} />
            </p>
            <div className={'bg-text-contrast rounded-[5px] shadow-sm overflow-x-auto'}>
                <table className={'w-full text-sm'}>
                    <thead>
                        <tr className={'border-b border-foreground/10'}>
                            <th className={headerCellClass}>{t('vouchers_codes.columns.code')}</th>
                            <th className={headerCellClass}>{t('vouchers_codes.columns.purchaser')}</th>
                            <th className={headerCellClass}>{t('vouchers_codes.prepaid_list.paid')}</th>
                            <th className={headerCellClass}>{t('vouchers_codes.prepaid_list.refunded')}</th>
                            <th className={headerCellClass}>{t('vouchers_codes.columns.remaining_balance')}</th>
                            <th className={headerCellClass}>{t('vouchers_codes.prepaid_list.status_column')}</th>
                            <th className={headerCellClass}>{t('vouchers_codes.prepaid_list.vat')}</th>
                            <th className={headerCellClass}>{t('vouchers_codes.columns.purchased')}</th>
                            <th className={headerCellClass}>{t('vouchers_codes.columns.expiry')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {vouchers.map((v) => {
                            const p = v.prepaid;
                            const expanded = canViewHistory && expandedId === v.id;
                            return (
                                <Fragment key={v.id}>
                                    <tr
                                        className={cn(
                                            'border-b border-foreground/5 last:border-b-0',
                                            canViewHistory && 'cursor-pointer hover:bg-foreground/[0.02]',
                                            p && p.status !== 'active' && 'text-foreground/60',
                                        )}
                                        onClick={onRowClick(() => setExpandedId((cur) => (cur === v.id ? null : v.id)))}
                                    >
                                        <td className={cellClass}>{v.code ?? naLabel}</td>
                                        <td className={cellClass}>
                                            {v.purchaserName ?? v.purchaserEmail ?? naLabel}
                                            {p && !p.hasPayment && (
                                                <span className={'ml-2 text-xs font-medium text-foreground border border-foreground/30 rounded px-1.5 py-0.5'}>
                                                    {t('vouchers_codes.prepaid_list.no_payment')}
                                                </span>
                                            )}
                                        </td>
                                        <td className={cellClass}>{formatMoney(p?.paymentReceived ?? null, v.currency)}</td>
                                        <td className={cellClass}>
                                            {/* #3332 D6: nothing refunded is 0,00 €, a value — not "n/a". */}
                                            {p ? formatMoney(p.refundedAmount, v.currency) : naLabel}
                                        </td>
                                        <td className={cellClass}>{formatMoney(v.remaining ?? v.discountValue, v.currency)}</td>
                                        <td className={cellClass}>
                                            {p ? t(`vouchers_codes.prepaid_list.status.${p.status}`) : naLabel}
                                        </td>
                                        <td className={cellClass}>{vatLabel(v)}</td>
                                        <td className={cellClass}>{formatDate(v.createdAt)}</td>
                                        <td className={cellClass}>{formatDate(v.validUntilDate)}</td>
                                    </tr>
                                    {expanded && (
                                        <tr className={'border-b border-foreground/5 last:border-b-0'}>
                                            <td colSpan={COLUMNS} className={'bg-foreground/[0.02]'}>
                                                <VoucherStatementPanel
                                                    voucherId={v.id}
                                                    currency={v.currency}
                                                    naLabel={naLabel}
                                                    formatMoney={formatMoney}
                                                    formatDate={formatDate}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            );
                        })}
                        {vouchers.length === 0 && (
                            <tr>
                                <td colSpan={COLUMNS} className={'px-4 py-3'}>
                                    <span className={'text-sm text-foreground/40 italic'}>{t('vouchers_codes.empty')}</span>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <VoucherLiabilityReport formatMoney={formatMoney} naLabel={naLabel} />
        </>
    );
};

export default PrepaidVouchersTab;
